import { useTranslation } from 'react-i18next';

import type { ICensusInfo } from './components/CensusLoadedRow';
import CensusLoadedRow from './components/CensusLoadedRow';
import CensusUploadTrigger from './components/CensusUploadTrigger';
import RadioCard from '../../../../../../../shared/components/Cards/RadioCard/RadioCard';
import CustomInput from '../../../../../../../shared/components/Inputs/CustomInput/CustomInput';
import { INPUT_ACTION, AVG_EMPLOYEE_AGE } from '../../../../../../../shared/constants/tagManager';
import TagManagerService from '../../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_QUOTES } from '../../../../../../../shared/translations/quotes';
import type { AgeInputMode } from '../../../../../../../shared/types/quotes';
import { MAX_AGE, MIN_AGE, type IGroupSelection } from '../../../../../../../shared/utils/quotes/quotes';

export interface IProductRowAgeSectionProps {
  selection: IGroupSelection;
  addonAgeRange: { min: number; max: number } | null;
  averageAge: string;
  averageAgeError?: string | null;
  onAverageAgeChange: (val: string) => void;
  onAgeInputModeChange: (mode: AgeInputMode) => void;
  bandLabel: string | null;
  censusInfo: ICensusInfo | null;
  onRemoveCensus: () => void;
  onOpenCensusModal: () => void;
}

export default function ProductRowAgeSection({
  selection,
  addonAgeRange,
  averageAge,
  averageAgeError,
  onAverageAgeChange,
  onAgeInputModeChange,
  bandLabel,
  censusInfo,
  onRemoveCensus,
  onOpenCensusModal
}: Readonly<IProductRowAgeSectionProps>) {
  const { t } = useTranslation();

  return (
    <div className="quote-product-row__age">
      <span className="quote-product-row__age-label">
        {t(TRANSLATIONS_QUOTES.create.row.ageData)} <span className="quote-product-row__req">*</span>
      </span>
      <div
        className="quote-product-row__radio-group"
        role="radiogroup"
        aria-label={t(TRANSLATIONS_QUOTES.create.row.ageData)}
      >
        <RadioCard
          selected={selection.ageInputMode === 'average'}
          label={t(TRANSLATIONS_QUOTES.create.row.enterAverageAge)}
          onSelect={() => onAgeInputModeChange('average')}
        >
          {selection.ageInputMode === 'average' && (
            <>
              <CustomInput
                type="number"
                min={addonAgeRange?.min ?? MIN_AGE}
                max={addonAgeRange?.max ?? MAX_AGE}
                aria-label={t(TRANSLATIONS_QUOTES.create.group.averageAge)}
                placeholder="e.g. 38"
                value={averageAge}
                onChange={(event) => onAverageAgeChange(event.target.value)}
                error={averageAgeError ?? undefined}
                containerClassName="quote-product-row__age-input"
                onBlur={() => {
                  TagManagerService.pushEvent(INPUT_ACTION, AVG_EMPLOYEE_AGE);
                }}
              />
              {bandLabel && !averageAgeError && (
                <div className="quote-product-row__age-band">
                  {t(TRANSLATIONS_QUOTES.create.row.ageBand, { band: bandLabel })}
                </div>
              )}
            </>
          )}
        </RadioCard>

        <RadioCard
          selected={selection.ageInputMode === 'census'}
          label={t(TRANSLATIONS_QUOTES.create.row.uploadQuickCensus)}
          tag={t(TRANSLATIONS_QUOTES.create.row.uploadQuickCensusHint)}
          onSelect={() => onAgeInputModeChange('census')}
        >
          {selection.ageInputMode === 'census' && (
            <>
              <div className="quote-product-row__age-method-helper">
                {t(TRANSLATIONS_QUOTES.create.row.uploadQuickCensusHelper)}
              </div>
              {censusInfo ? (
                <CensusLoadedRow censusInfo={censusInfo} onRemove={onRemoveCensus} />
              ) : (
                <CensusUploadTrigger onOpen={onOpenCensusModal} />
              )}
            </>
          )}
        </RadioCard>
      </div>
    </div>
  );
}
